import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { saveAs } from "file-saver";
import formatDate from "../../../utils/formatDate";
import formatKeys from "../../../utils/formatKeys";
import useModalStrip from "../../../global-hooks/useModalStrip";

const ResponseListData = ({ data }) => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const { showModalStrip } = useModalStrip();

  const [currentData, setCurrentData] = useState(data?.responses || []);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loader, setLoader] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const NODE_URL = process.env.REACT_APP_IMG_URL;
  const LAST_DOWNLOAD_KEY = `form_${formId}_last_download`;

  const lastDownloadedAt = localStorage.getItem(LAST_DOWNLOAD_KEY);

  /* ================= SEARCH ================= */
  const resDataKeys = useMemo(() => {
    return data?.responses?.[0]?.resData
      ? Object.keys(data.responses[0].resData)
      : [];
  }, [data]);

  console.log("resDataKeys", resDataKeys);


  const handleSearch = (value) => {
    const searchText = value.toLowerCase().trim();
    if (!searchText) {
      setCurrentData(data?.responses || []);
      return;
    }

    const filtered = data?.responses?.filter((item) =>
      resDataKeys.some((key) =>
        String(item?.resData?.[key] || "")
          .toLowerCase()
          .includes(searchText)
      )
    );

    setCurrentData(filtered);
  };

  /* ================= CHECKBOX HANDLERS ================= */
  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? currentData.map((r) => r._id) : []);
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  /* ================= DOWNLOAD ================= */
  const handleDownloadZIP = async () => {
    const { value } = await Swal.fire({
      title: "Download ZIP",
      input: "radio",
      inputOptions: {
        all: "All",
        new: "Only New",
        selected: "Only Selected",
      },
      inputValidator: (value) => !value && "Please choose one option",
      confirmButtonText: "Download",
      showCancelButton: true,
    });

    if (!value) return;

    if (value === "selected" && selectedIds.length === 0) {
      Swal.fire("No Selection", "Please select records first", "warning");
      return;
    }

    const payload = {
      formId,
      type: value,
      responseIds: value === "selected" ? selectedIds : undefined,
      afterDate: value === "new" ? lastDownloadedAt : undefined,
    };

    try {
      setLoader(true);
      const token = localStorage.getItem("userToken");

      const res = await axios.post(
        `${API_BASE_URL}/download-zip`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status !== "SUCCESS") {
        throw new Error(res.data.message);
      }

      const file = await axios.get(
        `${NODE_URL}${res.data.filePath}`,
        { responseType: "blob" }
      );

      saveAs(file.data, "responses.zip");

      localStorage.setItem(LAST_DOWNLOAD_KEY, new Date().toISOString());

      Swal.fire("Success", "ZIP downloaded successfully", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Download failed", "error");
    } finally {
      setLoader(false);
    }
  };

  /* ================= ALL DYNAMIC KEYS ================= */
  const allKeys = Object.keys(
    currentData.reduce((acc, row) => ({ ...acc, ...row?.resData }), {})
  ).filter((key) => key !== "Profile");

  return (
    <Paper sx={{ maxHeight: "80vh", overflow: "auto" }}>
      {/* ===== TOP BAR ===== */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          bgcolor: "#fff",
          borderBottom: "1px solid #ddd",
          p: 2,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <TextField
          size="small"
          placeholder="Search responses..."
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 260 } }}
        />

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            onClick={handleDownloadZIP}
            disabled={loader}
          >
            {loader ? "Downloading..." : "Download ZIP"}
          </Button>

          <Button variant="outlined" onClick={() => navigate("/publish-template")}>
            Back
          </Button>
        </Box>
      </Box>

      {/* ===== TABLE ===== */}
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <input
                  type="checkbox"
                  checked={
                    currentData.length > 0 &&
                    selectedIds.length === currentData.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>

              <TableCell>Sr</TableCell>
              <TableCell>Image</TableCell>

              {allKeys.map((key) => (
                <TableCell key={key} sx={{ fontWeight: "bold" }}>
                  {key.toUpperCase()}
                </TableCell>
              ))}

              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {currentData.map((response, index) => (
              <TableRow key={response._id} hover>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(response._id)}
                    onChange={() => handleSelectOne(response._id)}
                  />
                </TableCell>

                <TableCell>{index + 1}</TableCell>

                <TableCell
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    window.open(
                      `${NODE_URL}/${response?.uploadImgRes?.[0]?.fileUrl}`,
                      "_blank"
                    )
                  }
                >
                  <Box
                    component="img"
                    src={
                      response?.uploadImgRes?.length
                        ? `${NODE_URL}/${response.uploadImgRes[0].fileUrl}`
                        : "/placeholder.png"
                    }
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: "cover",
                      borderRadius: 1,
                      border: "1px solid #ddd",
                    }}
                  />
                </TableCell>

                {allKeys.map((key) => (
                  <TableCell key={key}>
                    {formatKeys(response?.resData?.[key])}
                  </TableCell>
                ))}

                <TableCell>{formatDate(response.updatedAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ResponseListData;
